import React, { useRef } from "react";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { Button, Box, Typography, TextField } from "@mui/material";
import MailIcon from "@mui/icons-material/MailOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import CircularProgressOverlay from "../Table/CircularProgressOverlay";
import validationsService from "../../services/validationService";
import Modal from "../Modal";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles(theme => ({
  icon: {
    marginRight: theme.spacing(2)
  },
  checkmark: {
    height: theme.spacing(4),
    width: theme.spacing(4)
  },
  contentText: {
    marginBottom: theme.spacing(3)
  },
  cancelButton: {
    marginRight: theme.spacing(2)
  },
  inputRoot: {
    height: 54
  },
  input: {
    paddingTop: theme.spacing(2),
    backgroundColor: "none",
    height: 38,
    marginRight: 0
  }
}));

export default function EmailModal(props) {
  const theme = useTheme();
  const classes = useStyles();
  const [email, setEmail] = React.useState(props.defaultEmailAddress);
  const [emailSubmitted, setEmailSubmitted] = React.useState(false);
  const [submitClicked, setSubmitClicked] = React.useState(false);
  const [invalidEmail, setInvalidEmail] = React.useState(true);
  const { t } = useTranslation();
  const invalidEmailMessage = t("emailModal.invalidEmailMessage");
  const emailModalTitle = t("emailModal.emailModalTitle", {
    type:
      props.emailModalType === "certificate"
        ? t("emailModal.certificate")
        : t("emailModal.report")
  });
  const emailModalContent =
    props.emailModalType === "certificate"
      ? t("emailModal.emailModalContentCert")
      : t("emailModal.emailModalContent");
  const emailInput = useRef(null);

  function handleSubmit(e) {
    setSubmitClicked(true);
    const valid = validationsService.checkValidEmail(email);
    setInvalidEmail(!valid);
    if (valid) {
      setEmailSubmitted(true);
      props.onSubmit(e, email, props.trainingRecordId);
    } else {
      emailInput.current.focus();
    }
  }

  function handleEmailInput(event) {
    setEmail(event.target.value);
  }

  function getDialogContent() {
    if (!props.emailSuccesfullySent && !emailSubmitted) {
      return (
        <React.Fragment>
          <Typography
            classes={{ root: classes.contentText }}
            variant="body2"
            color="textPrimary"
          >
            {emailModalContent}
          </Typography>

          <TextField
            InputProps={{
              classes: {
                root: classes.inputRoot,
                inputMarginDense: classes.input
              }
            }}
            error={invalidEmail && submitClicked}
            helperText={
              invalidEmail && submitClicked ? invalidEmailMessage : ""
            }
            autoFocus
            margin="dense"
            id="name"
            label={t("emailModal.email")}
            type="email"
            fullWidth
            variant="filled"
            defaultValue={props.defaultEmailAddress}
            onChange={handleEmailInput}
            inputRef={emailInput}
          />
        </React.Fragment>
      );
    } else if (props.emailSuccesfullySent === "yes") {
      return (
        <React.Fragment>
          <Box
            color={theme.palette.text.secondary}
            display="flex"
            flexDirection="row"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <CheckCircleOutlinedIcon
              color="primary"
              classes={{ root: `${classes.icon} ${classes.checkmark}` }}
            />
            <Typography variant="h4" color="textSecondary">
              {t("emailModal.successMsg")}
            </Typography>
          </Box>
        </React.Fragment>
      );
    } else if (props.emailSuccesfullySent === "no") {
      return (
        <React.Fragment>
          <Box
            color={theme.palette.text.secondary}
            display="flex"
            flexDirection="row"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <ErrorIcon
              color="error"
              classes={{ root: `${classes.icon} ${classes.checkmark}` }}
            />
            <Typography variant="h4" color="textSecondary">
              {t("emailModal.errorMsg")}
            </Typography>
          </Box>
        </React.Fragment>
      );
    } else if (!props.emailSuccesfullySent && emailSubmitted) {
      return (
        <Box display="flex" height="100%">
          <CircularProgressOverlay />
        </Box>
      );
    }
  }

  function getDialogTitle() {
    return (
      <Box display="flex">
        <MailIcon classes={{ root: classes.icon }} />
        <Typography variant="h4">{emailModalTitle}</Typography>
      </Box>
    );
  }

  function getDialogActions() {
    if (!props.emailSuccesfullySent && !emailSubmitted) {
      return (
        <div>
          <Button
            onClick={props.onClose}
            color="primary"
            classes={{ root: classes.cancelButton }}
          >
            {t("cancelBtn")}
          </Button>
          <Button onClick={handleSubmit} color="primary" variant="contained">
            {t("emailModal.sendEmailBtn")}
          </Button>
        </div>
      );
    } else if (props.emailSuccesfullySent) {
      return (
        <Button
          onClick={props.onButtonClose}
          color="primary"
          variant="contained"
        >
          {t("closeBtn")}
        </Button>
      );
    }
  }

  return (
    <Modal
      maxWidth="xs"
      dialogTitle={getDialogTitle()}
      dialogContent={getDialogContent()}
      dialogActions={getDialogActions()}
      open={props.open}
      onClick={e => e.stopPropagation()}
      onClose={props.onClose}
      data-qa="email-modal"
    />
  );
}

EmailModal.propTypes = {
  defaultEmailAddress: PropTypes.string,
  emailModalType: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  emailSuccesfullySent: PropTypes.string // 'true' if email was successfully sent, 'false' if there was an error
};
